import { useState, useEffect, useRef, useCallback } from "react";
import { Modal, Form, Input, message, Upload, Button, Image, Select, Row, Col, InputNumber, Spin, } from "antd";
import { ShowToast, Severty } from "../../helper/toast";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { uploadFileToS3 } from "../../helper/uploadImage";
import { UploadOutlined } from "@ant-design/icons";
import useApi from "../../hooks/useApi";
import { getBelts, getBranches, getStudents, updateCustomer, } from "../../constants/API/API";
import callApi from "../../constants/API/CallApi";

const PAGE_SIZE = 10;

const FileType = ["image/png", "image/jpg", "image/jpeg", "image/avif", "image/webp", "image/gif",];

const CustomerUpdate = ({ editVisible, setEditVisible, selectedCustomer, refresh, }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState([]);
  const [image, setImage] = useState([]);
  const [branches, setBranches] = useState([]);
  const [belts, setBelts] = useState([]);
  const [type, setType] = useState("parent");
  const [self, setSelf] = useState(true);
  const [phone, setPhone] = useState({ mobileNumber: "", countryCode: "", });
  const [selectedBranchId, setSelectedBranchId] = useState(null);

  // Paginated parent dropdown state
  const [parents, setParents] = useState([]);
  const [parentSearch, setParentSearch] = useState("");
  const [parentPage, setParentPage] = useState(1);
  const [parentTotal, setParentTotal] = useState(0);
  const [parentLoading, setParentLoading] = useState(false);
  const searchTimerRef = useRef(null);

  const fetchParents = useCallback(async ({ page = 1, search = "", append = false } = {}) => {
    setParentLoading(true);
    try {
      const res = await getStudents({ roleType: "parent", page, limit: PAGE_SIZE, search });
      const { data: list = [], total = 0 } = res?.data || {};
      setParentTotal(total);
      setParents((prev) => append ? [...prev, ...list] : list);
      setParentPage(page);
    } catch (err) {
      console.error("Failed to fetch parents", err);
    } finally {
      setParentLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedCustomer && editVisible) {
      setSelectedBranchId(selectedCustomer.branchId);
    }
  }, [selectedCustomer, editVisible]);

  useApi({
    callback: getBranches,
    dependencies: [refresh],
    shouldCallApi: true,
    onSuccess: ({ data }) => {
      const activeBranches = (data || []).filter((branch) => branch.isActive);
      setBranches(activeBranches);
    },
  });

  // Initial load — runs on mount and whenever refresh changes
  useEffect(() => {
    fetchParents({ page: 1, search: "", append: false });
  }, [refresh]);


  // Debounced search handler for parent Select
  const handleParentSearch = (value) => {
    setParentSearch(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      fetchParents({ page: 1, search: value, append: false });
    }, 400);
  };

  // Infinite-scroll handler — load more when near the bottom
  const handleParentPopupScroll = (e) => {
    const { target } = e;
    const isNearBottom =
      target.scrollTop + target.offsetHeight >= target.scrollHeight - 20;
    const hasMore = parents.length < parentTotal;
    if (isNearBottom && hasMore && !parentLoading) {
      fetchParents({ page: parentPage + 1, search: parentSearch, append: true });
    }
  };

  useApi({
    callback: getBelts,
    payload: selectedBranchId ? { branchId: selectedBranchId } : null,
    dependencies: [selectedBranchId],
    shouldCallApi: !!selectedBranchId,
    onSuccess: ({ data }) => {
      setBelts(data);
    },
  });

  useEffect(() => {
    if (selectedCustomer && editVisible) {
      const mobileNumber = selectedCustomer.mobileNumber || "";
      const profilePic = selectedCustomer.profilePic || "";
      const countryCode = selectedCustomer.countryCode || "";

      setPhone({ mobileNumber, countryCode });
      setType(selectedCustomer.roleType || "parent");

      form.setFieldsValue({
        ...selectedCustomer,
        gender: selectedCustomer.gender || "",
        parentId: "self",
        mobileNumber: "+" + countryCode + mobileNumber,
        profilePic: profilePic,
      });

      if (profilePic) {
        const fileData = {
          uid: "1",
          name: "Profile Pic",
          status: "done",
          url: profilePic,
          thumbUrl: profilePic,
        };
        setFile([fileData]);
        setImage(profilePic);
      }
    }
  }, [selectedCustomer, editVisible, form]);

  const handleOk = async (values) => {
    setLoading(true);
    callApi({
      callback: updateCustomer,
      payload: [
        {
          ...values,
          ...phone,
          profilePic: file.length > 0 ? file[0].url : null,
        },
        selectedCustomer._id,
      ],
      onSuccess: ({ message }) => {
        ShowToast(message, Severty.SUCCESS);
        refresh(new Date().getTime());
        setEditVisible(false);
        form.resetFields();
        setFile([]);
        setImage([]);
        setLoading(false);
      },
      onError: (err) => {
        ShowToast(err, Severty.ERROR);
        setLoading(false);
      },
    });
  };

  const handleCancel = () => {
    setEditVisible(false);
    form.resetFields();
    setFile([]);
    setImage([]);
    setPhone({ mobileNumber: "", countryCode: "" });
  };

  const validateName = (_, value) => {
    if (!value) {
      return Promise.reject("Please enter a name");
    } else if (!/^[A-Z][a-zA-Z ]*$/.test(value)) {
      return Promise.reject(
        "Name must start with a capital letter and contain only letters"
      );
    }
    return Promise.resolve();
  };

  const handleChangeMobileNumber = (value, data) => {
    const countryCode = data?.dialCode;
    const mobileNumber = value.slice(countryCode?.length);
    setPhone({ mobileNumber, countryCode });
  };

  const beforeUpload = (file) => {
    if (!FileType.includes(file.type)) {
      message.error("File format is not correct");
      return false;
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error("Image must be smaller than 5 MB!");
      return false;
    }
    return true;
  };

  const handleChange = async (event) => {
    const { file } = event;
    setLoading(true);
    uploadFileToS3({ file, dirName: "tutorly/profile" })
      .then((data) => {
        const fileData = {
          uid: file.uid,
          name: file.name,
          status: "done",
          url: data.Location,
          thumbUrl: data.Location,
        };
        setFile([fileData]);
        setImage(data.Location);
        form.setFieldsValue({ profilePic: data.Location });
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  };

  const handleRemove = async () => {
    setFile([]);
    setImage("");
  };

  return (
    <Modal
      width={800}
      centered
      open={editVisible}
      className="tab_modal"
      title="Update Customer"
      okButtonProps={{
        htmlType: "submit",
        form: "update-customer",
        loading: loading,
      }}
      onCancel={handleCancel}
    >
      <Form
        id="update-customer"
        form={form}
        layout="vertical"
        onFinish={handleOk}
      >
        <Row gutter={[12, 0]}>
          <Col span={24} md={12}>
            <Form.Item
              name="roleType"
              label="Role"
              rules={[{ required: true, message: "Please select role!" }]}
            >
              <Select
                onChange={(v) => setType(v)}
                placeholder="Select Role Type"
              >
                <Select.Option key={"student"}>Student</Select.Option>
                <Select.Option key={"parent"}>Parent</Select.Option>
              </Select>
            </Form.Item>
          </Col>

          {type === "student" && (
            <Col span={24} md={12}>
              <Form.Item
                name="parentId"
                label="Parent"
                rules={[{ required: true, message: "Please select parent!" }]}
              >
                <Select
                  showSearch
                  filterOption={false}
                  placeholder="Search & select parent"
                  onSearch={handleParentSearch}
                  onPopupScroll={handleParentPopupScroll}
                  notFoundContent={parentLoading ? <Spin size="small" /> : "No parents found"}
                  dropdownRender={(menu) => (
                    <>
                      {menu}
                      {parentLoading && parents.length > 0 && (
                        <div style={{ textAlign: "center", padding: "8px 0" }}>
                          <Spin size="small" />
                        </div>
                      )}
                      {!parentLoading && parents.length > 0 && (
                        <div style={{ textAlign: "center", padding: "4px 0", color: "#999", fontSize: 12 }}>
                          {parents.length} / {parentTotal} loaded
                        </div>
                      )}
                    </>
                  )}
                  onChange={(v) => setSelf(v === "self")}
                >
                  {parents?.map((parent, idx) => {
                    if (idx === 0) {
                      return <Select.Option key={"self"}>Self</Select.Option>;
                    }
                    return (
                      <Select.Option key={parent?._id} value={parent?._id}>
                        {parent?.name}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>
          )}

          <Col span={24} md={12}>
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: "Please enter the name" }]}
            >
              <Input placeholder="Enter name" />
            </Form.Item>
          </Col>

          <Col span={24} md={12}>
            <Form.Item
              name="gender"
              label="Gender"
            >
              <Select placeholder="Select Gender" allowClear>
                <Select.Option value="male">Male</Select.Option>
                <Select.Option value="female">Female</Select.Option>
                <Select.Option value="other">Other</Select.Option>
              </Select>
            </Form.Item>
          </Col>


          {type === "parent" && (
            <>
              <Col span={24} md={12}>
                <Form.Item
                  name="email"
                  label="Email"
                  rules={[
                    { required: true, message: "Please enter the email" },
                    {
                      type: "email",
                      message: "Please enter a valid email address",
                    },
                  ]}
                >
                  <Input placeholder="Enter email" />
                </Form.Item>
              </Col>

              <Col span={24} md={12}>
                <Form.Item
                  name="mobileNumber"
                  label="Mobile Number"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value) {
                          return Promise.reject(
                            new Error("Please enter the phone number")
                          );
                        }
                        if (value.length < 10) {
                          return Promise.reject(
                            new Error("Phone number must be at least 8-12 digits")
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <PhoneInput
                    countryCode="91"
                    countryCodeEditable={false}
                    country={"ca"}
                    value={
                      phone
                        ? (phone.countryCode ? phone.countryCode : "+1") +
                        (phone.mobileNumber ? phone.mobileNumber : null)
                        : ""
                    }
                    onChange={handleChangeMobileNumber}
                  />
                </Form.Item>
              </Col>
            </>
          )}

          {((type === "student" && !!self) || type === "parent") && (
            <>
              <Col span={24} sm={12} md={12}>
                <Form.Item
                  name="branchId"
                  label="Branch"
                  rules={[{ required: true, message: "Please select branch!" }]}
                >
                  <Select
                    placeholder="Select Branch"
                    onChange={(value) => {
                      setSelectedBranchId(value);
                      form.setFieldsValue({ beltId: undefined });
                      setBelts([]);
                    }}  >
                    {branches?.map((branch) => (
                      <Select.Option key={branch?._id} value={branch?._id}>
                        {branch?.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>

              </Col>
            </>
          )}

          {type === "student" && (
            <Col span={24} md={12}>
              <Form.Item
                name="age"
                label="Age"
                rules={[{ required: true, message: "Please enter age" }]}
              >
                <InputNumber
                  type="number"
                  min={3}
                  max={100}
                  placeholder="Enter age"
                />
              </Form.Item>
            </Col>
          )}

          {type !== "parent" && (
            <Col span={24} md={12}>
              <Form.Item
                name="limit"
                label="Class Limit"
                rules={[
                  { required: true, message: "Please enter class limit!" },
                ]}
              >
                <InputNumber
                  type="number"
                  min={1}
                  max={100}
                  placeholder="Enter Class Limit"
                />
              </Form.Item>
            </Col>
          )}

          {type !== "parent" && (
            <Col span={24} sm={12} md={12}>
              <Form.Item
                name="beltId"
                label="Belt"
                rules={[{ required: true, message: "Please select a belt!" }]}
              >
                <Select placeholder="Select Belt">
                  {belts?.map((belt) => (
                    <Select.Option key={belt?._id} value={belt?._id}>
                      {belt?.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          )}

          <Col span={24}>
            <Form.Item label="Upload Profile" name="profilePic">
              <Upload
                listType="picture"
                maxCount={1}
                beforeUpload={beforeUpload}
                customRequest={handleChange}
                onRemove={handleRemove}
                fileList={file}
                action=""
              >
                {file && file.length > 0 ? null : (
                  <Button icon={<UploadOutlined />}>Upload</Button>
                )}
              </Upload>
              {image && typeof image === "string" && image !== "" && (
                <div className="mt-3">
                  <Image width={100} src={image} />
                </div>
              )}
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default CustomerUpdate;
